import { Platform } from 'react-native';

// Lazy-load native Google Sign-In to avoid crashes on web/Expo Go
let GoogleSignin: any = null;
let statusCodes: any = {};
if (Platform.OS !== 'web') {
  try {
    const mod = require('@react-native-google-signin/google-signin');
    GoogleSignin = mod.GoogleSignin;
    statusCodes = mod.statusCodes;
  } catch {
    // Native module unavailable
  }
}

// Google OAuth configuration
const GOOGLE_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ?? '';
const GOOGLE_IOS_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID ?? '';

// Track if GSI script is loaded
let gsiLoaded = false;
let gsiLoadPromise: Promise<void> | null = null;

export type GoogleAuthResult =
  | { success: true; idToken: string }
  | { success: false; cancelled?: boolean; error?: string };

/**
 * Configure Google Sign-In for native platforms.
 * Call this once at app startup (in _layout.tsx).
 */
export function configureGoogleSignIn(): void {
  if (Platform.OS === 'web' || !GoogleSignin) return;

  // Only configure if we have valid client IDs
  if (!GOOGLE_WEB_CLIENT_ID && !GOOGLE_IOS_CLIENT_ID) {
    console.warn('Google Sign-In: No client IDs configured');
    return;
  }

  try {
    GoogleSignin.configure({
      webClientId: GOOGLE_WEB_CLIENT_ID,
      iosClientId: GOOGLE_IOS_CLIENT_ID,
      scopes: ['email', 'profile'],
    });
  } catch (err) {
    console.error('Google Sign-In configuration error:', err);
  }
}

/**
 * Check if Google Sign-In is available on the current platform.
 */
export function isGoogleSignInAvailable(): boolean {
  if (Platform.OS === 'web') {
    // Web OAuth is available if we have a web client ID
    return Boolean(GOOGLE_WEB_CLIENT_ID);
  }
  // Native requires the module and a client ID
  return Boolean(GoogleSignin && (GOOGLE_WEB_CLIENT_ID || GOOGLE_IOS_CLIENT_ID));
}

/**
 * Perform Google Sign-In and return the ID token.
 * Handles both web and native platforms.
 */
export async function signInWithGoogle(): Promise<GoogleAuthResult> {
  if (Platform.OS === 'web') {
    return signInWithGoogleWeb();
  }
  return signInWithGoogleNative();
}

/**
 * Native Google Sign-In using @react-native-google-signin/google-signin
 */
async function signInWithGoogleNative(): Promise<GoogleAuthResult> {
  if (!GoogleSignin) {
    return { success: false, error: 'Google Sign-In is not available. Please use a development build.' };
  }

  if (!GOOGLE_WEB_CLIENT_ID && !GOOGLE_IOS_CLIENT_ID) {
    return { success: false, error: 'Google Sign-In is not configured.' };
  }

  try {
    // Check Play Services (Android only, iOS returns immediately)
    if (Platform.OS === 'android') {
      await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
    }

    // Attempt sign-in - returns user info including idToken in newer versions
    const userInfo = await GoogleSignin.signIn();

    // Try to get idToken from signIn response first (newer API)
    let idToken = userInfo?.data?.idToken || userInfo?.idToken;

    // Fallback to getTokens() if idToken not in response (older API)
    if (!idToken) {
      const tokens = await GoogleSignin.getTokens().catch(() => null);
      idToken = tokens?.idToken;
    }

    if (!idToken) {
      return { success: false, error: 'No ID token received from Google.' };
    }

    return { success: true, idToken };
  } catch (err: any) {
    // Handle specific error codes
    if (err?.code === statusCodes.SIGN_IN_CANCELLED) {
      return { success: false, cancelled: true };
    }
    if (err?.code === statusCodes.IN_PROGRESS) {
      return { success: false, error: 'Sign-in already in progress.' };
    }
    if (err?.code === statusCodes.PLAY_SERVICES_NOT_AVAILABLE) {
      return { success: false, error: 'Google Play Services is not available.' };
    }

    return { success: false, error: err?.message || 'Google sign-in failed. Please try again.' };
  }
}

/**
 * Load Google Identity Services script for web
 */
function loadGsiScript(): Promise<void> {
  if (gsiLoaded) return Promise.resolve();
  if (gsiLoadPromise) return gsiLoadPromise;

  gsiLoadPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Not in browser environment'));
      return;
    }

    // Check if already loaded
    if ((window as any).google?.accounts?.id) {
      gsiLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      gsiLoaded = true;
      resolve();
    };
    script.onerror = () => {
      gsiLoadPromise = null;
      reject(new Error('Failed to load Google Sign-In'));
    };
    document.head.appendChild(script);
  });

  return gsiLoadPromise;
}

/**
 * Web Google Sign-In using Google Identity Services
 */
async function signInWithGoogleWeb(): Promise<GoogleAuthResult> {
  if (!GOOGLE_WEB_CLIENT_ID) {
    return { success: false, error: 'Google Sign-In is not configured for web.' };
  }

  try {
    await loadGsiScript();

    return new Promise((resolve) => {
      const google = (window as any).google;

      if (!google?.accounts?.id) {
        resolve({ success: false, error: 'Google Sign-In failed to load.' });
        return;
      }

      google.accounts.id.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        callback: (response: { credential?: string; error?: string }) => {
          if (response.credential) {
            resolve({ success: true, idToken: response.credential });
          } else {
            resolve({ success: false, error: response.error ?? 'Google sign-in failed.' });
          }
        },
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true, // Enable FedCM
      });

      // Use prompt() for One Tap or fallback popup
      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // One Tap not available or skipped, use OAuth popup flow
          useOAuthPopup(resolve);
        } else if (notification.isDismissedMoment()) {
          if (notification.getDismissedReason() === 'credential_returned') {
            // Success - callback will handle it
          } else {
            // User dismissed, try popup as fallback
            useOAuthPopup(resolve);
          }
        }
      });
    });
  } catch (err: any) {
    console.error('Google Sign-In web error:', err);
    return { success: false, error: 'Google sign-in failed. Please try again.' };
  }
}

/**
 * Fallback OAuth popup for when One Tap is not available
 * Uses implicit flow with a popup window
 */
function useOAuthPopup(resolve: (result: GoogleAuthResult) => void): void {
  // Check if we're already handling a callback (hash in current URL)
  if (window.location.hash && window.location.hash.includes('id_token')) {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const idToken = params.get('id_token');
    // Clear the hash
    window.history.replaceState(null, '', window.location.pathname);
    if (idToken) {
      resolve({ success: true, idToken });
      return;
    }
  }

  const nonce = generateNonce();
  // Use current URL as redirect so we can catch the response
  const redirectUri = window.location.origin + window.location.pathname;

  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', GOOGLE_WEB_CLIENT_ID);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'id_token');
  authUrl.searchParams.set('scope', 'openid email profile');
  authUrl.searchParams.set('nonce', nonce);
  authUrl.searchParams.set('prompt', 'select_account');

  const width = 500;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    authUrl.toString(),
    'google-signin',
    `width=${width},height=${height},left=${left},top=${top},popup=true`
  );

  if (!popup) {
    resolve({ success: false, error: 'Popup blocked. Please allow popups and try again.' });
    return;
  }

  let resolved = false;

  // Listen for messages from the popup
  const messageHandler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === 'google-auth-callback') {
      resolved = true;
      window.removeEventListener('message', messageHandler);
      popup.close();
      if (event.data.idToken) {
        resolve({ success: true, idToken: event.data.idToken });
      } else if (event.data.error) {
        resolve({ success: false, error: event.data.error });
      } else {
        resolve({ success: false, cancelled: true });
      }
    }
  };
  window.addEventListener('message', messageHandler);

  // Also poll for redirect (fallback if postMessage doesn't work)
  const checkInterval = setInterval(() => {
    if (resolved) {
      clearInterval(checkInterval);
      return;
    }

    try {
      if (popup.closed) {
        clearInterval(checkInterval);
        window.removeEventListener('message', messageHandler);
        if (!resolved) {
          resolve({ success: false, cancelled: true });
        }
        return;
      }

      // Check if redirected back to our origin
      if (popup.location.origin === window.location.origin) {
        const hash = popup.location.hash;
        if (hash && hash.includes('id_token')) {
          resolved = true;
          clearInterval(checkInterval);
          window.removeEventListener('message', messageHandler);

          const params = new URLSearchParams(hash.substring(1));
          const idToken = params.get('id_token');
          const error = params.get('error');
          popup.close();

          if (idToken) {
            resolve({ success: true, idToken });
          } else if (error) {
            resolve({ success: false, error: `Google sign-in failed: ${error}` });
          } else {
            resolve({ success: false, error: 'No token received from Google.' });
          }
        }
      }
    } catch {
      // Cross-origin error - popup is still on Google's domain, keep waiting
    }
  }, 200);

  // Timeout after 5 minutes
  setTimeout(() => {
    if (!resolved) {
      clearInterval(checkInterval);
      window.removeEventListener('message', messageHandler);
      if (!popup.closed) {
        popup.close();
      }
      resolve({ success: false, error: 'Sign-in timed out. Please try again.' });
    }
  }, 5 * 60 * 1000);
}

/**
 * Generate a random nonce for OAuth requests
 */
function generateNonce(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let nonce = '';
  for (let i = 0; i < 32; i++) {
    nonce += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return nonce;
}
